import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSimulation } from '../../contexts/SimulationContext';
import { SortConfig } from '../DataTable';
import useSimulations, { FilterConfig } from '../../hooks/useSimulations';
import useTrialExpirationHandler from '../../hooks/useTrialExpirationHandler';
import { useSubscriptionExpirationHandler } from '../../hooks/useSubscriptionExpirationHandler';
import { usePageTitle } from '../../hooks';
import { logger } from '../../utils/logger';
import axios from 'axios';
import OnboardingModal from '../OnboardingModal';
import TrialStatusModal from '../TrialStatusModal';
import SubscriptionExpiredModal from '../SubscriptionExpiredModal';
import { useCreateWithTrialCheck } from '../../hooks/useCreateWithTrialCheck';
import SubscriptionCancellationBanner from '../SubscriptionCancellationBanner';
import ResourceLimitCard from '../common/ResourceLimitCard';
import CollapsibleInfoCard from '../common/CollapsibleInfoCard';
import SimulationFilters from './SimulationFilters';
import SimulationsList from './SimulationsList';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const SimulationsContainer: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'createdAt', direction: 'desc' });
  const [filterConfig, setFilterConfig] = useState<FilterConfig>({});
  const [showFilters, setShowFilters] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [orgInfo, setOrgInfo] = useState<any>(null);
  
  const { user } = useAuth();
  const navigate = useNavigate();
  const { dispatch } = useSimulation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Trial expiration handling
  const { 
    isExpiredModalOpen: isTrialStatusModalOpen, 
    isAdmin: isTrialAdmin, 
    handleApiError: handleTrialApiError, 
    closeExpiredModal: closeTrialStatusModal 
  } = useTrialExpirationHandler();

  // Subscription expiration handling
  const { 
    isExpiredModalOpen: isSubscriptionExpiredModalOpen, 
    isAdmin: isSubscriptionAdmin, 
    handleApiError: handleSubscriptionApiError, 
    closeExpiredModal: closeSubscriptionExpiredModal 
  } = useSubscriptionExpirationHandler();

  // Use the simulations hook
  const {
    simulations,
    loading,
    error,
    totalCount,
    refetch
  } = useSimulations({
    page,
    rowsPerPage,
    sortConfig,
    filterConfig,
    onTrialApiError: handleTrialApiError,
    onSubscriptionApiError: handleSubscriptionApiError
  });

  // Set page title
  usePageTitle('Simulations');

  // Check if user needs onboarding (no organization assigned)
  useEffect(() => {
    // Removed duplicate onboarding trigger - handled centrally in AuthContext
    // if (user && !user.organizationId) {
    //   setShowOnboarding(true);
    // } else if (user && user.organizationId) {
    if (user && user.organizationId) {
      // Fetch organization info
      fetchOrgInfo();
    }
  }, [user]);

  // Fetch organization info
  const fetchOrgInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/organization`);
      setOrgInfo(response.data.organization);
    } catch (error) {
      // Check if trial has expired or subscription is cancelled
      if (!handleTrialApiError(error)) {
        handleSubscriptionApiError(error);
      }
      logger.error('Error fetching organization info:', error);
    }
  };

  const {
    handleCreateAction,
    isTrialStatusModalOpen: createTrialStatusModalOpen,
    closeTrialStatusModal: closeCreateTrialStatusModal,
    isAdmin: createTrialAdmin
  } = useCreateWithTrialCheck();
  
  const handleNewSimulation = () => {
    handleCreateAction(() => {
      dispatch({ type: 'RESET_FORM' });
      navigate('/simulations/create');
    });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleSort = (key: string) => {
    setSortConfig(prevConfig => {
      if (prevConfig.key === key) {
        // Toggle direction if already sorting by this key
        return { 
          key, 
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc' 
        };
      }
      // Default to descending when sorting by a new key
      return { key, direction: 'desc' };
    });
  };
  
  const handleFilterChange = (key: keyof FilterConfig, value: any) => {
    setFilterConfig(prev => ({
      ...prev,
      [key]: value
    }));
    setPage(0); // Reset to first page when filters change
  };
  
  const clearFilters = () => {
    setFilterConfig({});
    setPage(0);
  };

  const handleToggleFilters = () => {
    setShowFilters(!showFilters);
  };

  if (loading && !isTrialStatusModalOpen && !isSubscriptionExpiredModalOpen) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 64px)' }}>
        <CircularProgress />
      </Box>
    );
  }

  // If onboarding is required, show the modal and a minimal view
  if (showOnboarding) {
    return <OnboardingModal isOpen={showOnboarding} onClose={() => setShowOnboarding(false)} />;
  }

  // If there's an error and no modal is active, show error message
  if (error && !isTrialStatusModalOpen && !isSubscriptionExpiredModalOpen) {
    return (
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={refetch} sx={{ mt: 2 }}>
          Retry
        </Button>
      </Box>
    );
  }
  
  // Main content when not loading, no error, and modals are not solely active
  return (
    <>
      {orgInfo?.status === 'ACTIVE_UNTIL' && (
         <SubscriptionCancellationBanner 
            isAdmin={user?.role === 'ADMIN'} 
            expiryDate={orgInfo.nextBillingAt} 
         />
      )}

      <TrialStatusModal 
        isOpen={isTrialStatusModalOpen} 
        onClose={closeTrialStatusModal} 
        isAdmin={isTrialAdmin}
        status="expired"
      />
      <SubscriptionExpiredModal 
        isOpen={isSubscriptionExpiredModalOpen} 
        onClose={closeSubscriptionExpiredModal} 
        isAdmin={isSubscriptionAdmin} 
      />
      
      {/* Trial expired modal for create button clicks */}
      <TrialStatusModal 
        isOpen={createTrialStatusModalOpen} 
        onClose={closeCreateTrialStatusModal} 
        isAdmin={createTrialAdmin}
        status="expired"
      />

      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 4, bgcolor: 'background.paper' }}>
        {/* Responsive Header */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'center' }, 
          mb: 3,
          gap: { xs: 2, sm: 0 }
        }}>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            component="h1" 
            gutterBottom={!isMobile}
            sx={{ mb: { xs: 0, sm: 1 } }}
          >
            Simulations
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleNewSimulation}
            fullWidth={isMobile}
            sx={{ height: 'fit-content' }}
          >
            New Simulation
          </Button>
        </Box>

        {/* Info */}
        <CollapsibleInfoCard
          title="About Simulations"
          content="Simulations predict audience reaction before you post. Choose a Social Account and platform, then run to see reputation and engagement forecasts, along with simulated replies."
        />

        {/* Quota card: simulations & ideas share the same monthly quota */}
        {orgInfo && (
          <ResourceLimitCard
            icon={<AssessmentIcon sx={{ color: 'primary.main' }} />}
            title="Simulations & Ideas"
            used={orgInfo.simUsed ?? 0}
            limit={orgInfo.plan === 'INTERNAL' ? null : (orgInfo.monthlySimQuota ?? null)}
            resetAt={orgInfo.plan === 'INTERNAL' ? null : (orgInfo.quotaResetAt ?? null)}
            isAdmin={user?.role === 'ADMIN'}
            sx={{ mb: 2 }}
          />
        )}

        {/* Filters */}
        <SimulationFilters
          filterConfig={filterConfig}
          showFilters={showFilters}
          onFilterChange={handleFilterChange}
          onToggleFilters={handleToggleFilters}
          onClearFilters={clearFilters}
        />

        {/* Simulations List */}
        <SimulationsList
          simulations={simulations}
          sortConfig={sortConfig}
          onSort={handleSort}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          onNewSimulation={handleNewSimulation}
        />
      </Paper>
    </>
  );
};

export default SimulationsContainer;